import {logger} from '../logger';
import React from 'react';
import {action, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import s from './s';
import {createProductInitCoordinator, removeProdLoadingElement, type ProductInitCoordinator, type ProductInitStatus} from './product-init/product-init';
import ProductInitError from './product-init/product-init-error';
import './product.scss';
import LayoutA from './layout-a';
import LayoutB from './redesign/layout-b';
import {isProductPageRedesign} from './redesign/arm';

import ModalSimple from '../global/modal-simple';
import * as emitter from '../global/emitter';
import AbTestStore from '../global/store/ab-test-store';
import ReturnPolicyModal from '../checkout/components/modals/return-policy.modal';
import {INST_PLATE_GATE_MESSAGE} from './components/engraved-plates/inst-plate/inst-plate-rules';
import {Portal} from '../util';
import PriceLiveStatus from './components/price-live-status';

@observer
class ProductComponent extends React.Component {
    public state: {initStatus: ProductInitStatus} = {
        initStatus: 'loading',
    };

    private readonly initCoordinator: ProductInitCoordinator;

    constructor(props) {
        super(props);
        s.isRetail = true;
        makeObservable(this);
        window.react_product = this;
        this.initCoordinator = createProductInitCoordinator({
            resetStore: () => s.resetInitState(),
            initStore: () => s.init(),
            removeLoader: () => removeProdLoadingElement(),
            logError: error => logger.error(error),
            setStatus: initStatus => this.setState({initStatus}),
        });
    }

    @action
    ab_update(_key: string, _value: number) {
        window.ab = window.ab || {};
        window.ab[_key] = _value;
        if (!AbTestStore.ab) {
            AbTestStore.ab = {};
        }
        AbTestStore.ab[_key] = _value;
        emitter.eventEmitter.dispatch(emitter.EventType.AB, 'ab');
        this.forceUpdate();
    }

    componentDidMount() {
        this.init();
    }

    init() {
        this.initCoordinator.run();
    }

    retryInit = () => {
        this.initCoordinator.run();
    };

    render() {
        if (this.state.initStatus === 'error') {
            return <ProductInitError onRetry={this.retryInit} />;
        }

        if (this.state.initStatus !== 'ready' || !s || !s.si) {
            return null;
        }

        return (
            <div>
                <PriceLiveStatus />
                {isProductPageRedesign() ? <LayoutB /> : <LayoutA />}
                <ModalSimple
                    id="modal-fast-ship-warning"
                    title="Fast Shipping Removed"
                >
                    <div className="text-left">
                        <p>If you customize this product it may not qualify for fast shipping.</p>
                        <div className="mt-2">
                            <span style={{paddingRight: 6}}>
                                <i className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`} />
                            </span>
                            Indicates the frame material that qualifies for Fast Shipping. If no icon, the product will ship with standard methods.
                        </div>
                    </div>
                </ModalSimple>
                <ModalSimple
                    id="modal-level-lock"
                    title="Level-Lock Frame Hanging System"
                >
                    <div>
                        <p>
                            The <strong>Level-Lock&reg; Hanging System</strong> is the ultimate solution for professionally hanging your framed documents, art, and wall decor with ease and precision. Included with every premium diploma frame or custom frame from Design Your Frame, Level-Lock&reg;
                            ensures your frames stay securely in place and perfectly straight. This innovative system eliminates the frustration of crooked or misaligned frames, giving you the confidence to create a polished and professional look every time.
                        </p>
                        <p>
                            Whether you’re showcasing a single diploma or creating a stunning gallery wall of awards, certificates, and artwork, Level-Lock&reg; makes it simple to align multiple frames and achieve an eye-catching arrangement. Each system comes with all the tools you need for
                            accurate, even hanging, so you can transform your home or office into a space that celebrates your achievements and personal style. With Level-Lock&reg;, hanging your frames is not just easier—it’s better!
                        </p>

                        <picture>
                            <source
                                srcSet="https://dfstatic.com/images/cached/general/levellockvideobutton.webp"
                                type="image/webp"
                            />
                            <img
                                className="resp"
                                src="https://dfstatic.com/images/cached/general/levellockvideobutton.jpg"
                                alt="level lock video button"
                            />
                        </picture>

                        <div style={{marginTop: '1rem'}}>
                            <a
                                href="/level-lock-hanging-system"
                                className="link underline"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="View level-lock frame hanging information"
                            >
                                Level-Lock Frame Hanging System
                            </a>
                        </div>
                    </div>
                </ModalSimple>
                <ModalSimple
                    id="modal-ep-template-error"
                    title="Personalization Required"
                >
                    <div className="text-left">
                        <p>{s.isInstPlate ? INST_PLATE_GATE_MESSAGE : 'Please complete or correct the highlighted plate lines before adding to cart.'}</p>
                    </div>
                </ModalSimple>
                <ReturnPolicyModal />
            </div>
        );
    }
}

export default ProductComponent;
